import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, X, Send, CheckCircle } from 'lucide-react';

const SupportChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('welcome'); // welcome, email, message, sending, done
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    setStep('sending');
    try {
      await axios.post(`${API}/support/contact`, { email, message });
      setStep('done');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
      setStep('message');
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    setStep('welcome');
    setEmail('');
    setMessage('');
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="support-chat-btn"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-xl text-white flex items-center justify-center hover:scale-110 transition-transform z-50"
          style={{ backgroundColor: 'rgb(26, 160, 157)' }}
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border z-50 overflow-hidden" data-testid="support-chat-window">
          {/* Header */}
          <div className="p-4 text-white flex items-center justify-between" style={{ backgroundColor: 'rgb(26, 160, 157)' }}>
            <div>
              <h3 className="font-bold text-sm">Stray Spotter Support</h3>
              <p className="text-xs opacity-80">We'll get back to you ASAP</p>
            </div>
            <button onClick={resetChat} className="hover:bg-white/20 rounded-full p-1 transition-colors" data-testid="close-support-chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 min-h-[250px] flex flex-col">
            {step === 'welcome' && (
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="bg-gray-100 rounded-xl p-3 mb-3 inline-block">
                    <p className="text-sm text-gray-800">Hi there! How can we help you today?</p>
                  </div>
                  <div className="bg-gray-100 rounded-xl p-3 inline-block">
                    <p className="text-sm text-gray-800">Please share your email and describe your issue, and we'll get back to you as soon as possible.</p>
                  </div>
                </div>
                <Button
                  onClick={() => setStep('email')}
                  data-testid="start-support-msg"
                  className="w-full mt-4 text-white"
                  style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                >
                  Write a Message
                </Button>
              </div>
            )}

            {step === 'email' && (
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="bg-gray-100 rounded-xl p-3 mb-3 inline-block">
                    <p className="text-sm text-gray-800">What's your email address so we can get back to you?</p>
                  </div>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="support-email-input"
                    className="mt-2"
                  />
                </div>
                <Button
                  onClick={() => { if (email) setStep('message'); else toast.error('Please enter your email'); }}
                  data-testid="support-next-btn"
                  className="w-full mt-4 text-white"
                  style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                  disabled={!email}
                >
                  Next
                </Button>
              </div>
            )}

            {step === 'message' && (
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="bg-gray-100 rounded-xl p-3 mb-3 inline-block">
                    <p className="text-sm text-gray-800">Please describe your issue or question:</p>
                  </div>
                  <textarea
                    placeholder="Describe your issue here..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="support-message-input"
                    className="w-full mt-2 p-3 border rounded-lg text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  />
                </div>
                <Button
                  onClick={handleSubmit}
                  data-testid="support-send-btn"
                  className="w-full mt-4 text-white"
                  style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                  disabled={!message.trim() || loading}
                >
                  <Send className="w-4 h-4 mr-2" /> Send Message
                </Button>
              </div>
            )}

            {step === 'sending' && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: 'rgb(26, 160, 157)', borderTopColor: 'transparent' }}></div>
                  <p className="text-sm text-gray-600">Sending your message...</p>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p className="font-bold text-gray-800 mb-1">Message Sent!</p>
                  <p className="text-sm text-gray-600 mb-4">We'll get back to you at {email}</p>
                  <Button onClick={resetChat} variant="outline" size="sm" data-testid="close-after-send">Close</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatWidget;
